import React, { useState, useEffect } from 'react';
import {
  Card, Table, Tag, Statistic, Row, Col, Spin, message, Tabs, Descriptions, Badge, Empty, Typography, List, Drawer, Button, Modal, Form, Input, Space, Popconfirm, Switch,
} from 'antd';
import {
  UserOutlined, FileTextOutlined, TeamOutlined, PieChartOutlined, PlusOutlined, EditOutlined, DeleteOutlined, QuestionCircleOutlined,
} from '@ant-design/icons';
import { SurveyRecord, DIMENSION_COLORS, DIMENSION_NAMES } from '../types';
import * as api from '../api';
import HappinessWheel from '../components/HappinessWheel';

const { Text } = Typography;

const AdminPage: React.FC = () => {
  const [users, setUsers] = useState<any[]>([]);
  const [stats, setStats] = useState<Record<string, any>>({});
  const [allSurveys, setAllSurveys] = useState<SurveyRecord[]>([]);
  const [questionnaires, setQuestionnaires] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<{ user: any; records: SurveyRecord[] } | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 问卷编辑
  const [modalOpen, setModalOpen] = useState(false);
  const [editingQ, setEditingQ] = useState<any>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    loadAdminData();
  }, []);

  const loadAdminData = async () => {
    setLoading(true);
    try {
      const [usersData, surveysData, qsData] = await Promise.all([
        api.adminGetUsers(),
        api.adminGetAllSurveys(),
        api.getQuestionnaires(),
      ]);
      setUsers(usersData.users);
      setStats(usersData.stats);
      setAllSurveys(surveysData);
      setQuestionnaires(qsData);
    } catch (err: any) {
      message.error('加载管理数据失败');
    } finally {
      setLoading(false);
    }
  };

  const openUserDetail = async (userId: string) => {
    try {
      const data = await api.adminGetUserSurveys(userId);
      setSelectedUser(data);
      setDrawerOpen(true);
    } catch {
      message.error('加载用户详情失败');
    }
  };

  // ── 问卷管理 ──
  const openQuestionnaireModal = (q?: any) => {
    setEditingQ(q || null);
    if (q) {
      form.setFieldsValue({
        name: q.name,
        description: q.description,
        dimensions: JSON.stringify(q.dimensions, null, 2),
        active: q.active,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        dimensions: JSON.stringify([
          { code: 'dim1', name: '维度一', color: '#2ECC71', questions: ['题目1', '题目2'] },
          { code: 'dim2', name: '维度二', color: '#3498DB', questions: ['题目1'] },
        ], null, 2),
        active: true,
      });
    }
    setModalOpen(true);
  };

  const saveQuestionnaire = async () => {
    try {
      const values = await form.validateFields();
      let dimensions;
      try {
        dimensions = JSON.parse(values.dimensions);
        if (!Array.isArray(dimensions)) throw new Error();
      } catch {
        message.error('维度JSON格式错误');
        return;
      }

      const payload = {
        name: values.name,
        description: values.description || '',
        dimensions,
        active: values.active,
      };

      if (editingQ) {
        await api.updateQuestionnaire(editingQ.id, payload);
        message.success('问卷已更新');
      } else {
        await api.createQuestionnaire(payload);
        message.success('问卷已创建');
      }
      setModalOpen(false);
      loadAdminData();
    } catch (err: any) {
      if (err?.response) message.error(err.response.data?.message || '保存失败');
    }
  };

  const deleteQuestionnaire = async (id: string) => {
    try {
      await api.deleteQuestionnaire(id);
      message.success('问卷已删除');
      loadAdminData();
    } catch {
      message.error('删除失败');
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  const totalSurveys = allSurveys.length;
  const totalUsers = users.length;
  const avgScore = totalSurveys > 0
    ? Math.round(allSurveys.reduce((s, r) => s + r.totalScore, 0) / totalSurveys)
    : 0;

  // 用户表格列
  const userColumns = [
    {
      title: '用户名', dataIndex: 'username', key: 'username',
      render: (name: string, record: any) => (
        <Button type="link" onClick={() => openUserDetail(record.id)} style={{ padding: 0 }}>
          <UserOutlined style={{ marginRight: 6 }} />{name}
        </Button>
      ),
    },
    { title: '昵称', dataIndex: 'displayName', key: 'displayName' },
    {
      title: '角色', dataIndex: 'role', key: 'role',
      render: (role: string) => <Tag color={role === 'admin' ? 'red' : 'blue'}>{role === 'admin' ? '管理员' : '用户'}</Tag>,
    },
    { title: '问卷数', key: 'count', render: (_: any, r: any) => stats[r.id]?.total || 0 },
    {
      title: '平均分', key: 'avg',
      render: (_: any, r: any) => stats[r.id] ? <Badge count={stats[r.id].avg} showZero color="#2ECC71" overflowCount={90} style={{ backgroundColor: '#2ECC71' }} /> : '-',
    },
    { title: '最新', key: 'latest', render: (_: any, r: any) => stats[r.id]?.latest || '-' },
    { title: '注册', dataIndex: 'createdAt', key: 'createdAt', render: (d: string) => d?.slice(0, 10) },
  ];

  // 问卷管理表格列
  const qColumns = [
    { title: '名称', dataIndex: 'name', key: 'name' },
    { title: '维度', dataIndex: 'dimensions', key: 'dims', render: (dims: any[]) => dims?.length || 0 },
    { title: '总题数', dataIndex: 'questionCount', key: 'qCount' },
    {
      title: '状态', dataIndex: 'active', key: 'active',
      render: (active: boolean) => active ? <Tag color="green">启用</Tag> : <Tag>停用</Tag>,
    },
    {
      title: '操作', key: 'actions',
      render: (_: any, record: any) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openQuestionnaireModal(record)}>编辑</Button>
          {record.id !== 'default-les-v1' && (
            <Popconfirm title="确认删除此问卷？" onConfirm={() => deleteQuestionnaire(record.id)}>
              <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 概览卡片 */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={12} sm={6}>
          <Card hoverable onClick={() => openUserDetail('')} style={{ cursor: 'default' }}>
            <Statistic title="总用户数" value={totalUsers} prefix={<TeamOutlined />} valueStyle={{ color: '#3498DB' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="问卷总数" value={totalSurveys} prefix={<FileTextOutlined />} valueStyle={{ color: '#2ECC71' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="平均分" value={avgScore} suffix="/ 90" prefix={<PieChartOutlined />} valueStyle={{ color: '#F39C12' }} />
          </Card>
        </Col>
        <Col xs={12} sm={6}>
          <Card>
            <Statistic title="问卷套数" value={questionnaires.length} prefix={<QuestionCircleOutlined />} valueStyle={{ color: '#9B59B6' }} />
          </Card>
        </Col>
      </Row>

      <Card>
        <Tabs
          defaultActiveKey="users"
          items={[
            {
              key: 'users',
              label: <><TeamOutlined /> 用户管理</>,
              children: (
                <Table dataSource={users} columns={userColumns} rowKey="id" pagination={{ pageSize: 10, showTotal: t => `共 ${t} 个用户` }} size="small" />
              ),
            },
            {
              key: 'questionnaires',
              label: <><QuestionCircleOutlined /> 问卷管理</>,
              children: (
                <>
                  <div style={{ marginBottom: 16 }}>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openQuestionnaireModal()}>新建问卷</Button>
                  </div>
                  <Table dataSource={questionnaires} columns={qColumns} rowKey="id" pagination={false} size="small" />
                </>
              ),
            },
            {
              key: 'surveys',
              label: <><FileTextOutlined /> 全部问卷结果</>,
              children: (
                <Table
                  dataSource={allSurveys}
                  columns={[
                    { title: '日期', dataIndex: 'surveyDate', key: 'date', width: 110 },
                    {
                      title: '用户', dataIndex: 'userId', key: 'userId', ellipsis: true,
                      render: (id: string) => {
                        const u = users.find(u => u.id === id);
                        return <span title={id}>{u?.username || id.slice(0, 8) + '...'}</span>;
                      },
                    },
                    {
                      title: '总分', dataIndex: 'totalScore', key: 'totalScore', sorter: (a: any, b: any) => a.totalScore - b.totalScore,
                      render: (s: number) => {
                        const color = s >= 72 ? '#2ECC71' : s >= 54 ? '#F39C12' : '#E91E63';
                        return <span style={{ color, fontWeight: 600 }}>{s}</span>;
                      },
                    },
                    ...['身体健康', '心理健康', '关系健康', '事业发展', '财富自由', '价值意义'].map((name, i) => {
                      const code = ['physical', 'mental', 'relationship', 'career', 'wealth', 'meaning'][i];
                      return {
                        title: name, key: code, width: 80,
                        render: (_: any, r: SurveyRecord) => <Tag color={DIMENSION_COLORS[code]} style={{ minWidth: 30, textAlign: 'center' }}>{r.dimensionScores[code]}</Tag>,
                      };
                    }),
                    { title: '时间', dataIndex: 'createdAt', key: 'createdAt', width: 90, render: (d: string) => d?.slice(11, 19) },
                  ]}
                  rowKey="id"
                  pagination={{ pageSize: 15, showTotal: t => `共 ${t} 条` }}
                  size="small"
                  scroll={{ x: 900 }}
                />
              ),
            },
          ]}
        />
      </Card>

      {/* 用户详情抽屉 */}
      <Drawer
        title={selectedUser ? `用户: ${selectedUser.user?.displayName || selectedUser.user?.username}` : ''}
        placement="right" width={640} onClose={() => setDrawerOpen(false)} open={drawerOpen}
      >
        {selectedUser ? (
          <>
            <Descriptions column={2} size="small" bordered style={{ marginBottom: 16 }}>
              <Descriptions.Item label="用户名">{selectedUser.user?.username}</Descriptions.Item>
              <Descriptions.Item label="昵称">{selectedUser.user?.displayName}</Descriptions.Item>
              <Descriptions.Item label="角色"><Tag color={selectedUser.user?.role === 'admin' ? 'red' : 'blue'}>{selectedUser.user?.role === 'admin' ? '管理员' : '普通用户'}</Tag></Descriptions.Item>
              <Descriptions.Item label="注册时间">{selectedUser.user?.createdAt?.slice(0, 10)}</Descriptions.Item>
              <Descriptions.Item label="问卷数">{selectedUser.records.length}</Descriptions.Item>
            </Descriptions>
            {selectedUser.records.length > 0 && (
              <div style={{ height: 380, marginBottom: 16 }}>
                <Text strong>幸福之轮趋势</Text>
                <HappinessWheel records={selectedUser.records.slice(0, 6)} />
              </div>
            )}
            <List
              header={<Text strong>问卷记录</Text>}
              dataSource={selectedUser.records}
              renderItem={(record) => (
                <List.Item key={record.id}>
                  <List.Item.Meta
                    title={<span><Tag>{record.surveyDate}</Tag> 总分: <Text strong style={{ color: record.totalScore >= 72 ? '#2ECC71' : '#F39C12' }}>{record.totalScore}</Text></span>}
                    description={<div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginTop: 4 }}>{Object.entries(record.dimensionScores).map(([code, score]) => <Tag key={code} color={DIMENSION_COLORS[code]} style={{ fontSize: 11 }}>{DIMENSION_NAMES[code]}: {score}</Tag>)}</div>}
                  />
                </List.Item>
              )}
            />
          </>
        ) : <Empty description="请从用户列表中选择一个用户" />}
      </Drawer>

      {/* 问卷编辑弹窗 */}
      <Modal
        title={editingQ ? '编辑问卷' : '新建问卷'}
        open={modalOpen}
        onOk={saveQuestionnaire}
        onCancel={() => setModalOpen(false)}
        width={720}
        okText="保存"
        cancelText="取消"
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="问卷名称" rules={[{ required: true, message: '请输入问卷名称' }]}>
            <Input placeholder="例如：LES 生命质量现状测评" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea rows={2} placeholder="简短描述此问卷的用途" />
          </Form.Item>
          <Form.Item name="active" label="状态" valuePropName="checked">
            <Switch checkedChildren="启用" unCheckedChildren="停用" />
          </Form.Item>
          <Form.Item
            name="dimensions"
            label="维度与题目（JSON格式）"
            rules={[{ required: true, message: '请输入维度JSON' }]}
            extra={
              <span style={{ fontSize: 12, color: '#999' }}>
                格式：[{'{ "code": "dim1", "name": "维度名", "color": "#2ECC71", "questions": ["题目1", "题目2"] }'}]
              </span>
            }
          >
            <Input.TextArea rows={10} style={{ fontFamily: 'monospace', fontSize: 12 }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default AdminPage;
